import {
  type ColorValue,
  Platform,
  type StyleProp,
  StyleSheet,
  type ViewStyle,
} from "react-native";
import { createNanoIconSet } from "react-native-nano-icons";

import { cn } from "@/lib/ui/cn";

import glyphMap from "../../../assets/icons/nanoicons/icons.glyphmap.json";

/**
 * The app's icons: Lucide glyphs compiled into one font by `react-native-nano-icons` (TRO-2483).
 *
 * Each export keeps its `lucide-react-native` name and props, so a call site reads the same as
 * before. The SVGs live in `assets/icons/glyphs`; to add an icon, save its SVG from lucide.dev
 * there, run `npx react-native-nano-icons --dynamic-app-config`, and export it below.
 *
 * Strokes are outlined when the font is built, so `strokeWidth` cannot vary at render time. A
 * heavier weight is its own glyph, `<name>--w<weight>` (`check--w3`, `arrow-up--w2_5`); any
 * other weight draws the default 2.
 *
 * The font is linked dynamically: it ships in the JS bundle rather than the binary, so an icon
 * change can go out over the air.
 */
const Nano = createNanoIconSet(
  glyphMap,
  Platform.OS === "web"
    ? require("../../../assets/icons/nanoicons/icons.woff2")
    : require("../../../assets/icons/nanoicons/icons.ttf"),
);

type GlyphName = keyof typeof glyphMap.i;

const isGlyph = (name: string): name is GlyphName => name in glyphMap.i;

export type IconGlyphProps = {
  size?: number;
  color?: ColorValue;
  strokeWidth?: number;
  style?: StyleProp<ViewStyle>;
  /** Web only; native ignores it, as it ignored it on Lucide's SVG. */
  className?: string;
  "aria-hidden"?: boolean;
  /** A glyph is decorative unless it is given a label. */
  accessibilityLabel?: string;
  accessible?: boolean;
  accessibilityElementsHidden?: boolean;
  testID?: string;
};

export type IconGlyph = ((props: IconGlyphProps) => React.JSX.Element) & { displayName?: string; };

function glyph(base: GlyphName): IconGlyph {
  function Glyph(
    { size = 24, color, strokeWidth, style, accessibilityLabel, testID, className }: IconGlyphProps,
  ) {
    const weighted = `${base}--w${String(strokeWidth).replace(".", "_")}`;
    const name: GlyphName = strokeWidth !== undefined && isGlyph(weighted) ? weighted : base;
    if (__DEV__ && strokeWidth !== undefined && strokeWidth !== 2 && name === base) {
      console.warn(`Icon "${base}" has no strokeWidth ${strokeWidth} glyph; drawing the default.`);
    }
    const hidden = accessibilityLabel === undefined;
    return (
      <Nano
        name={name}
        size={size}
        // Lucide never scaled with the text size, and layouts are sized for that.
        allowFontScaling={false}
        color={color}
        style={StyleSheet.flatten(style) ?? undefined}
        // Web draws the glyph in a `<span>`; stylesheets that size a row's label span exclude `.icon`.
        className={cn("icon", className)}
        testID={testID}
        accessible={!hidden}
        accessibilityLabel={accessibilityLabel}
        accessibilityElementsHidden={hidden}
        importantForAccessibility={hidden ? "no-hide-descendants" : "auto"}
      />
    );
  }
  Glyph.displayName = base;
  return Glyph;
}

export const Activity = glyph("activity");
export const AlertTriangle = glyph("triangle-alert");
export const ArchiveRestore = glyph("archive-restore");
export const ArrowDown = glyph("arrow-down");
export const ArrowDownLeft = glyph("arrow-down-left");
export const ArrowDownToLine = glyph("arrow-down-to-line");
export const ArrowDownUp = glyph("arrow-down-up");
export const ArrowLeft = glyph("arrow-left");
export const ArrowLeftRight = glyph("arrow-left-right");
export const ArrowUp = glyph("arrow-up");
export const ArrowUpDown = glyph("arrow-up-down");
export const ArrowUpRight = glyph("arrow-up-right");
export const Award = glyph("award");
export const BadgeCheck = glyph("badge-check");
export const Banknote = glyph("banknote");
export const BarChart3 = glyph("chart-column");
export const Barcode = glyph("barcode");
export const Bluetooth = glyph("bluetooth");
export const BluetoothOff = glyph("bluetooth-off");
export const BookOpen = glyph("book-open");
export const BoxSelect = glyph("square-dashed");
export const Building2 = glyph("building-2");
export const CalendarDays = glyph("calendar-days");
export const CalendarRange = glyph("calendar-range");
export const Camera = glyph("camera");
export const ChartColumn = glyph("chart-column");
export const ChartLine = glyph("chart-line");
export const Check = glyph("check");
export const CheckCircle2 = glyph("circle-check");
export const CheckSquare = glyph("square-check-big");
export const ChevronDown = glyph("chevron-down");
export const ChevronLeft = glyph("chevron-left");
export const ChevronRight = glyph("chevron-right");
export const ChevronUp = glyph("chevron-up");
export const CircleAlert = glyph("circle-alert");
export const CircleDollarSign = glyph("circle-dollar-sign");
export const ClipboardCheck = glyph("clipboard-check");
export const ClipboardList = glyph("clipboard-list");
export const Clock = glyph("clock");
export const Clock3 = glyph("clock-3");
export const Copy = glyph("copy");
export const CreditCard = glyph("credit-card");
export const Download = glyph("download");
export const Ellipsis = glyph("ellipsis");
export const ExternalLink = glyph("external-link");
export const Eye = glyph("eye");
export const EyeOff = glyph("eye-off");
export const FileText = glyph("file-text");
export const FileUp = glyph("file-up");
export const Filter = glyph("funnel");
export const Flame = glyph("flame");
export const Flashlight = glyph("flashlight");
export const FolderInput = glyph("folder-input");
export const FolderUp = glyph("folder-up");
export const FoldVertical = glyph("fold-vertical");
export const Gamepad2 = glyph("gamepad-2");
export const Gauge = glyph("gauge");
export const Gem = glyph("gem");
export const Globe = glyph("globe");
export const Globe2 = glyph("earth");
export const GripVertical = glyph("grip-vertical");
export const HandCoins = glyph("hand-coins");
export const Heart = glyph("heart");
export const HeartPulse = glyph("heart-pulse");
export const HelpCircle = glyph("circle-question-mark");
export const Image = glyph("image");
export const ImageOff = glyph("image-off");
export const ImagePlus = glyph("image-plus");
export const Info = glyph("info");
export const Keyboard = glyph("keyboard");
export const KeyRound = glyph("key-round");
export const Layers = glyph("layers");
export const Layers3 = glyph("layers");
export const LayoutGrid = glyph("layout-grid");
export const Lightbulb = glyph("lightbulb");
export const Link2 = glyph("link-2");
export const List = glyph("list");
export const ListChecks = glyph("list-checks");
export const ListFilter = glyph("list-filter");
export const Lock = glyph("lock");
export const LockKeyhole = glyph("lock-keyhole");
export const LockOpen = glyph("lock-open");
export const LogOut = glyph("log-out");
export const Mail = glyph("mail");
export const MapPin = glyph("map-pin");
export const MapPinned = glyph("map-pinned");
export const Maximize = glyph("maximize");
export const Maximize2 = glyph("maximize-2");
export const Minimize = glyph("minimize");
export const Minus = glyph("minus");
export const Moon = glyph("moon");
export const MoreHorizontal = glyph("ellipsis");
export const MousePointer2 = glyph("mouse-pointer-2");
export const MoveRight = glyph("move-right");
export const Network = glyph("network");
export const Package = glyph("package");
export const PackagePlus = glyph("package-plus");
export const PanelLeftClose = glyph("panel-left-close");
export const PanelLeftOpen = glyph("panel-left-open");
export const Pencil = glyph("pencil");
export const PenLine = glyph("pen-line");
export const PenTool = glyph("pen-tool");
export const Plus = glyph("plus");
export const Printer = glyph("printer");
export const QrCode = glyph("qr-code");
export const Receipt = glyph("receipt");
export const ReceiptText = glyph("receipt-text");
export const Redo2 = glyph("redo-2");
export const RefreshCw = glyph("refresh-cw");
export const Repeat = glyph("repeat");
export const Rocket = glyph("rocket");
export const RotateCcw = glyph("rotate-ccw");
export const Rows3 = glyph("rows-3");
export const Ruler = glyph("ruler");
export const ScanLine = glyph("scan-line");
export const ScanSearch = glyph("scan-search");
export const ScrollText = glyph("scroll-text");
export const Search = glyph("search");
export const Settings = glyph("settings");
export const Share2 = glyph("share-2");
export const ShieldCheck = glyph("shield-check");
export const ShoppingBag = glyph("shopping-bag");
export const ShoppingCart = glyph("shopping-cart");
export const Shuffle = glyph("shuffle");
export const SkipForward = glyph("skip-forward");
export const SlidersHorizontal = glyph("sliders-horizontal");
export const Sparkles = glyph("sparkles");
export const Split = glyph("split");
export const Square = glyph("square");
export const Star = glyph("star");
export const Store = glyph("store");
export const Sun = glyph("sun");
export const Tag = glyph("tag");
export const Tags = glyph("tags");
export const Ticket = glyph("ticket");
export const Trash2 = glyph("trash-2");
export const TrendingUp = glyph("trending-up");
export const TriangleAlert = glyph("triangle-alert");
export const Trophy = glyph("trophy");
export const Undo2 = glyph("undo-2");
export const UserRound = glyph("user-round");
export const Users = glyph("users");
export const WifiOff = glyph("wifi-off");
export const Workflow = glyph("workflow");
export const X = glyph("x");
export const XCircle = glyph("circle-x");
export const ZoomIn = glyph("zoom-in");
export const ZoomOut = glyph("zoom-out");
